import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";

function fmtDate(d: Date | string | null): string {
  return d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d)) : "—";
}
function fmtDateTime(d: string | null): string {
  return d ? new Intl.DateTimeFormat("tr-TR", { dateStyle: "short", timeStyle: "short" }).format(new Date(d)) : "—";
}
const nf = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 4 });

/** Mal Kabul Tutanağı (A4) — teslim alan firma künyesi + kalemler + teslim eden/alan imza-kaşe. */
export function GoodsReceiptPrintPage() {
  const [params] = useSearchParams();
  const id = params.get("id") ?? "";
  const q = trpc.inventory.receiptDetail.useQuery({ id }, { enabled: !!id });
  const d = q.data;
  const profQ = trpc.settings.companyProfile.get.useQuery(
    { tesisId: d?.facilityId ?? undefined },
    { enabled: !!d, staleTime: 5 * 60_000 },
  );
  const prof = profQ.data;

  return (
    <div className="grp-root">
      <style>{`
        .grp-root { --ink:#14253E; --line:#c9d2df; --muted:#5b6472; --soft:#f4f6fa; background:#fff; color:#1a2230; }
        .grp-page { max-width: 820px; margin: 0 auto; padding: 22px 26px 40px; font-family: 'Montserrat', system-ui, sans-serif; font-size: 12px; }
        .grp-num { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .grp-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; border-bottom: 3px solid var(--ink); padding-bottom: 12px; }
        .grp-brand { display:flex; align-items:center; gap:12px; }
        .grp-logo { max-height: 56px; max-width: 150px; object-fit: contain; }
        .grp-brand .n { font-size: 16px; font-weight: 800; color: var(--ink); }
        .grp-brand .a { font-size: 10.5px; color: var(--muted); margin-top:2px; max-width: 320px; line-height:1.35; }
        .grp-doc { text-align:right; }
        .grp-doc .t { font-size: 17px; font-weight: 800; color: var(--ink); letter-spacing:.02em; }
        .grp-doc .row { font-size: 11px; color: var(--muted); margin-top: 3px; }
        .grp-doc .row b { color: var(--ink); }
        .grp-parties { display:grid; grid-template-columns: 1fr 1fr; gap: 0; border:1px solid var(--line); border-top:none; }
        .grp-party { padding: 10px 14px; }
        .grp-party + .grp-party { border-left:1px solid var(--line); }
        .grp-party .lbl { font-size: 9px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color: var(--muted); margin-bottom: 4px; }
        .grp-party .nm { font-size: 13px; font-weight:700; color: var(--ink); }
        .grp-party .ln { font-size: 11px; color:#37414f; margin-top:2px; line-height:1.4; }
        .grp-meta { display:grid; grid-template-columns: repeat(4, 1fr); border:1px solid var(--line); border-top:none; }
        .grp-meta .c { padding: 7px 12px; }
        .grp-meta .c + .c { border-left:1px solid var(--line); }
        .grp-meta .k { font-size: 8.5px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color: var(--muted); }
        .grp-meta .v { font-size: 11.5px; font-weight:600; color: var(--ink); margin-top:1px; }
        .grp-sec { font-size: 11px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color: var(--ink); margin: 16px 0 6px; }
        table.grp-t { width:100%; border-collapse: collapse; font-size: 11px; }
        table.grp-t th { text-align:left; font-size: 9px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color:#fff; background: var(--ink); padding:6px 8px; }
        table.grp-t td { padding:6px 8px; border-bottom:1px solid #e7ebf1; }
        table.grp-t td.n, table.grp-t th.n { text-align:right; font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        table.grp-t tbody tr:nth-child(even) td { background:#fafbfd; }
        .grp-code { font-family:'JetBrains Mono', monospace; font-size: 9px; color: var(--muted); }
        .grp-note { font-size:11px; color:#37414f; margin-top:6px; border:1px solid var(--line); border-radius:6px; padding:9px 12px; }
        .grp-sign { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-top: 44px; }
        .grp-sign .box { border-top:1px solid var(--ink); padding-top:8px; font-size:10.5px; color: var(--muted); text-align:center; line-height:1.5; }
        .grp-sign .box b { display:block; font-size:12px; color: var(--ink); font-weight:700; margin-bottom:2px; }
        .grp-foot { margin-top: 22px; padding-top:8px; border-top:1px solid var(--line); font-size:9.5px; color:var(--muted); text-align:center; }
        .grp-toolbar { position: sticky; top: 0; z-index: 5; background:#fff; display:flex; gap:12px; align-items:center; justify-content:flex-end; padding: 10px 26px; border-bottom:1px solid var(--line); }
        .grp-btn { border:1px solid var(--ink); background: var(--ink); color:#fff; font-weight:600; font-size:13px; padding:7px 16px; border-radius:6px; cursor:pointer; }
        .grp-btn.sec { background:#fff; color: var(--ink); }
        .grp-btn:disabled { opacity:.5; cursor:default; }
        @media print {
          .grp-toolbar { display:none !important; }
          .grp-page { max-width:none; padding:0; }
          .grp-sign { margin-top: 56px; }
          .grp-sec, .grp-head { break-after: avoid; }
          tr { break-inside: avoid; }
        }
        @page { size: A4 portrait; margin: 12mm; }
      `}</style>

      <div className="grp-toolbar">
        <button type="button" className="grp-btn sec" onClick={() => window.close()}>Kapat</button>
        <button type="button" className="grp-btn" onClick={() => window.print()} disabled={!d}>Yazdır</button>
      </div>

      <div className="grp-page">
        {!id ? (
          <div style={{ color: "#b91c1c" }}>Geçersiz bağlantı — mal kabul kimliği yok.</div>
        ) : q.isLoading ? (
          <div style={{ color: "var(--muted)" }}>Tutanak hazırlanıyor…</div>
        ) : q.isError ? (
          <div style={{ color: "#b91c1c" }}>{q.error?.message}</div>
        ) : d ? (
          <>
            {/* Başlık: teslim alan firma + belge */}
            <div className="grp-head">
              <div className="grp-brand">
                {prof?.logoUrl ? <img className="grp-logo" src={prof.logoUrl} alt={prof.name ?? "Logo"} /> : null}
                <div>
                  <div className="n">{prof?.name || "STEPERP"}</div>
                  {prof?.address ? <div className="a">{prof.address}</div> : null}
                </div>
              </div>
              <div className="grp-doc">
                <div className="t">MAL KABUL TUTANAĞI</div>
                <div className="row">Fiş No: <b className="grp-num">{d.code}</b></div>
                <div className="row">Tarih: <b className="grp-num">{fmtDateTime(d.receivedAt)}</b></div>
              </div>
            </div>

            {/* Taraflar: teslim alan / teslim eden */}
            <div className="grp-parties">
              <div className="grp-party">
                <div className="lbl">Teslim Alan</div>
                <div className="nm">{prof?.name || "STEPERP"}</div>
                {prof?.address ? <div className="ln">{prof.address}</div> : null}
                {prof?.taxOffice || prof?.taxNumber ? <div className="ln">Vergi D./No: {[prof?.taxOffice, prof?.taxNumber].filter(Boolean).join(" · ")}</div> : null}
                {d.receivedBy ? <div className="ln">Teslim alan yetkili: {d.receivedBy}</div> : null}
              </div>
              <div className="grp-party">
                <div className="lbl">Teslim Eden</div>
                <div className="nm">{d.deliveredByName || d.supplier?.name || "—"}</div>
                {d.deliveredByPhone ? <div className="ln">Tel: {d.deliveredByPhone}</div> : null}
                {d.supplier?.name && d.deliveredByName ? <div className="ln">Tedarikçi: {d.supplier.name}</div> : null}
                {d.supplier?.phone && !d.deliveredByPhone ? <div className="ln">Tel: {d.supplier.phone}</div> : null}
              </div>
            </div>

            {/* Meta */}
            <div className="grp-meta">
              <div className="c"><div className="k">Fiş No</div><div className="v grp-num">{d.code}</div></div>
              <div className="c"><div className="k">Tarih</div><div className="v grp-num">{fmtDateTime(d.receivedAt)}</div></div>
              <div className="c"><div className="k">Lokasyon</div><div className="v">{d.locationName ?? "—"}</div></div>
              <div className="c"><div className="k">Kalem Sayısı</div><div className="v grp-num">{d.lines.length}</div></div>
            </div>

            {/* Kalemler */}
            <div className="grp-sec">Teslim Alınan Kalemler</div>
            <table className="grp-t">
              <thead>
                <tr>
                  <th style={{ width: 34 }}>#</th>
                  <th>Ürün</th>
                  <th className="n">Miktar</th>
                  <th>Birim</th>
                  <th>SKT</th>
                  <th>Lot / Tedarikçi Parti</th>
                </tr>
              </thead>
              <tbody>
                {d.lines.map((l, i) => (
                  <tr key={l.lotId}>
                    <td className="n">{i + 1}</td>
                    <td><span className="grp-code">{l.itemCode}</span> {l.itemName}</td>
                    <td className="n">{nf.format(l.qty)}</td>
                    <td>{l.uom}</td>
                    <td className="grp-num">{fmtDate(l.expiresAt)}</td>
                    <td><span className="grp-code">{l.lotCode}</span>{l.supplierLot ? ` · ${l.supplierLot}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {d.notes ? <div className="grp-note"><b>Not:</b> {d.notes}</div> : null}

            {/* İmza / kaşe */}
            <div className="grp-sign">
              <div className="box">
                <b>Teslim Eden</b>
                {d.deliveredByName || d.supplier?.name || "……………………………"}
                {d.deliveredByPhone ? <><br />{d.deliveredByPhone}</> : null}
                <br />(İmza)
              </div>
              <div className="box">
                <b>Teslim Alan</b>
                {d.receivedBy || prof?.name || "……………………………"}
                <br />(Kaşe / İmza)
              </div>
            </div>

            <div className="grp-foot">Bu tutanak {prof?.name || "STEPERP"} tarafından düzenlenmiştir · {d.code} · {fmtDateTime(d.receivedAt)}</div>
          </>
        ) : null}
      </div>
    </div>
  );
}
